import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { tenantSelector } from '../../selector/authenticationSelector';
import { isSelfServiceTenant } from '../../common/tenantStore';
import { RentalBookingDetails } from '../../types/bookingCreation';

interface Props {
    booking: Pick<
        RentalBookingDetails,
        'organizationName' | 'organizationUnitName' | 'costCenter' | 'attributionStatus'
    > | undefined;
}

/**
 * Who a booking is made for and the invoice recipient's cost centre (#1810),
 * on a booking's details page. Read-only; the edit forms use
 * `BookingAttributionFields`.
 *
 * Shown on operational tenants only, where the fields exist; an absent value
 * reads `n/a` rather than disappearing, so "none" and "not loaded" are not
 * confused with a missing feature.
 */
const BookingAttributionReadOnly: React.FC<Props> = ({ booking }) => {
    const { t } = useTranslation();
    const tenant = useSelector(tenantSelector);
    if (!isSelfServiceTenant(tenant) || !booking) return null;
    if (booking.attributionStatus === 'failed') {
        return (
            <p className={'text-sm text-foreground/70'}>
                {t('create_booking.attribution.load_failed')}
            </p>
        );
    }

    const organization = [booking.organizationName, booking.organizationUnitName]
        .filter((part): part is string => Boolean(part?.trim()))
        .join(' · ');

    return (
        <dl className={'grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm'}>
            <div className={'flex flex-col gap-1'}>
                <dt className={'text-foreground/60 font-medium'}>
                    {t('create_booking.attribution.organization')}
                </dt>
                <dd className={'text-foreground'}>{organization || t('common.not_applicable')}</dd>
            </div>
            <div className={'flex flex-col gap-1'}>
                <dt className={'text-foreground/60 font-medium'}>
                    {t('create_booking.attribution.cost_center')}
                </dt>
                <dd className={'text-foreground'}>
                    {booking.costCenter?.trim() || t('common.not_applicable')}
                </dd>
            </div>
        </dl>
    );
};

export default BookingAttributionReadOnly;
